import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Cpu, Loader2, MessageCircle, RefreshCw, ShieldAlert, ThumbsDown, ThumbsUp, TrendingUp, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

// Panel admin > Vanta AI > Vue d'ensemble : état du moteur (modèles et outils
// branchés), chiffres clés, activité des 14 derniers jours, modèles et réglages
// utilisés, avis 👍/👎 et personnes les plus actives (lien vers leurs conversations).
// Les abonnés et les offres sont dans Clients > Vanta AI.

const MODEL_NAMES = { pulse: "Pulse", vector: "Vector" };
const TOOL_NAMES = { search: "Recherche web", image: "Création d'images", model3d: "Création 3D (Blender)" };
const EFFORT_NAMES = { minimal: "Minimal", moyen: "Moyen", eleve: "Élevé", tres_eleve: "Très élevé", ultra: "Ultra" };
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const euros = (n) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

function Status({ on, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs ${on ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/[0.03] text-white/45"}`}>
      {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{label}
    </span>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium flex items-center gap-2"><Icon className="w-4 h-4 text-white/60" />{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

// Barres horizontales : part de chaque élément (modèles, réglages).
function Shares({ items, names }) {
  const total = items.reduce((sum, [, n]) => sum + n, 0);
  if (!total) return <p className="text-sm text-white/40">Rien cette semaine.</p>;
  return (
    <div className="space-y-2">
      {items.map(([key, n]) => (
        <div key={key}>
          <div className="flex justify-between text-xs mb-1"><span className="text-white/75">{names[key] || key}</span><span className="text-white/45">{n} · {Math.round((n / total) * 100)} %</span></div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-violet-400/70" style={{ width: `${(n / total) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function VantaAIPanel({ hdr: parentHdr, onOpenChats, onOpenModeration }) {
  const hdrRef = useRef(parentHdr);
  hdrRef.current = parentHdr;
  const hdr = useCallback(() => hdrRef.current(), []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return axios.get(`${API}/admin/vai`, { headers: hdr() }).then((r) => setData(r.data))
      .catch((err) => toast.error(errorText(err, "Chargement impossible"))).finally(() => setLoading(false));
  }, [hdr]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  const e = data.engine;
  const peak = Math.max(1, ...data.daily.map((d) => d.count));
  const ratings = data.ratings_week.up + data.ratings_week.down;
  return (
    <div className="space-y-5" data-testid="vai-admin">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-white/50">L'assistant IA de la page /ai : Pulse (gratuit) et Vector (payant) tournent sur le moteur installé sur le VPS.</p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Actualiser</Button>
      </div>

      <Card title="Moteur" icon={Cpu}>
        {!e.configured ? (
          <p className="text-sm text-amber-200">Pas configuré : sur le VPS, lance « sudo bash /opt/vantapulse/deploy/migrate.sh » (il remet le moteur en route tout seul).</p>
        ) : !e.online ? (
          <p className="text-sm text-red-200">Injoignable : vérifie que le moteur tourne sur le VPS et que son adresse est bonne.</p>
        ) : null}
        <div className="flex flex-wrap gap-2">{Object.keys(MODEL_NAMES).map((m) => <Status key={m} on={e.models?.[m]} label={MODEL_NAMES[m]} />)}</div>
        <div className="flex flex-wrap gap-2">{Object.keys(TOOL_NAMES).map((t) => <Status key={t} on={e.tools?.[t]} label={TOOL_NAMES[t]} />)}<Status on={e.moderation} label="Modération (Llama Guard)" /></div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Abonnés Pro", data.subscribers.pro], ["Abonnés Max", data.subscribers.max], ["Conversations", data.chats], ["Réponses (7 j)", data.messages_week],
          ["Personnes actives (7 j)", data.active_users_week], ["Abonnements payés", data.paid], ["Revenu mensuel", euros(data.revenue_month)], ["Refus de la modération (7 j)", data.flags_week]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3"><p className="text-2xl font-semibold">{v}</p><p className="text-xs text-white/50">{l}</p></div>
        ))}
      </div>

      <Card title="Réponses par jour (14 jours)" icon={TrendingUp}>
        <div className="flex items-end gap-1 h-32" data-testid="vai-admin-daily">
          {data.daily.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full gap-1" title={`${new Date(d.day).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} : ${d.count}`}>
              <span className="text-[10px] text-white/40">{d.count || ""}</span>
              <div className="w-full rounded-t bg-violet-400/60 min-h-[2px]" style={{ height: `${(d.count / peak) * 100}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-white/35">
          <span>{new Date(data.daily[0].day).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span><span>aujourd'hui</span>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Card title="Modèles utilisés (7 j)" icon={Cpu}><Shares items={Object.entries(data.models_week).sort((a, b) => b[1] - a[1])} names={MODEL_NAMES} /></Card>
        <Card title="Niveau d'effort avec Vector (7 j)" icon={TrendingUp}><Shares items={Object.keys(EFFORT_NAMES).filter((k) => data.efforts_week[k]).map((k) => [k, data.efforts_week[k]])} names={EFFORT_NAMES} /></Card>
        <Card title="Avis sur les réponses (7 j)" icon={ThumbsUp}>
          {ratings === 0 ? <p className="text-sm text-white/40">Aucun avis cette semaine.</p> : (
            <>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-emerald-300"><ThumbsUp className="w-4 h-4" />{data.ratings_week.up}</span>
                <span className="flex items-center gap-1.5 text-red-300"><ThumbsDown className="w-4 h-4" />{data.ratings_week.down}</span>
                <span className="text-white/50">{Math.round((data.ratings_week.up / ratings) * 100)} % de satisfaits</span>
              </div>
              <div className="h-1.5 rounded-full bg-red-400/40 overflow-hidden"><div className="h-full bg-emerald-400/80" style={{ width: `${(data.ratings_week.up / ratings) * 100}%` }} /></div>
            </>
          )}
          <p className="text-xs text-white/40">Les 👎 se corrigent dans l'onglet Entraînement.</p>
        </Card>
        <Card title="Les plus actifs (7 j)" icon={MessageCircle}>
          {data.top_users.length === 0 ? <p className="text-sm text-white/40">Personne cette semaine.</p> : (
            <div className="divide-y divide-white/5">
              {data.top_users.map((u) => (
                <button key={u.user_id} type="button" onClick={() => onOpenChats?.(u)} className="w-full flex items-center justify-between gap-3 py-2 text-left hover:bg-white/[0.03] rounded px-1">
                  <span className="min-w-0"><span className="text-sm block truncate">{u.username || u.email}</span><span className="text-xs text-white/40 block truncate">{u.email}</span></span>
                  <span className="text-xs text-white/55 shrink-0">{u.answers} réponse{u.answers > 1 ? "s" : ""}</span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {data.flags_week > 0 && (
        <button type="button" onClick={onOpenModeration} className="w-full rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-left text-sm text-amber-100 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" />{data.flags_week} demande{data.flags_week > 1 ? "s" : ""} refusée{data.flags_week > 1 ? "s" : ""} par la modération cette semaine : voir l'onglet Modération.
        </button>
      )}
    </div>
  );
}
